import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import Pagination from '@/components/base/Pagination';
import SectionTitle from '@/components/base/SectionTitle';
import VideoCard from '@/components/feature/VideoCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getGenresByType, getVideosByType, getYears } from '@/utils/media';
import { videoTypeDescriptions, videoTypeLabels } from '@/mocks/videos';

const PAGE_SIZE = 10;
const SORTS = [
  { key: 'hot', label: '按热度' },
  { key: 'new', label: '按更新时间' },
  { key: 'rating', label: '按评分' },
] as const;

type SortKey = (typeof SORTS)[number]['key'];

export default function CategoryPage() {
  const { type = '' } = useParams();
  const typeLabel = videoTypeLabels[type] ?? '影视';
  const descriptionText =
    videoTypeDescriptions[type] ?? '汇聚精选影视资源，支持多维筛选与排序，快速找到你想看的作品。';

  useDocumentMeta(
    `【${typeLabel}】- 黄牛视频`,
    `${descriptionText}黄牛视频${typeLabel}频道提供高清${typeLabel}资源在线浏览与筛选。`,
    `${typeLabel},黄牛视频,${typeLabel}大全,高清${typeLabel},${typeLabel}资源`,
  );

  const [genre, setGenre] = useState('all');
  const [year, setYear] = useState('all');
  const [sort, setSort] = useState<SortKey>('hot');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const baseList = useMemo(() => getVideosByType(type), [type]);
  const genres = useMemo(() => getGenresByType(type), [type]);
  const years = useMemo(() => getYears(), []);

  useEffect(() => {
    setGenre('all');
    setYear('all');
    setSort('hot');
    setPage(1);
  }, [type]);

  const filtered = useMemo(() => {
    let list = baseList.filter((v) => {
      const okGenre = genre === 'all' || v.genres.includes(genre);
      const okYear = year === 'all' || String(v.year) === year;
      return okGenre && okYear;
    });
    list = [...list].sort((a, b) => {
      if (sort === 'rating') return b.rating - a.rating;
      if (sort === 'new') return a.updatedAt < b.updatedAt ? 1 : -1;
      return b.rating - a.rating || (a.updatedAt < b.updatedAt ? 1 : -1);
    });
    return list;
  }, [baseList, genre, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setGenre('all');
    setYear('all');
    setSort('hot');
    setPage(1);
  };

  const chipBase = 'rounded-full px-3 py-1.5 text-sm transition-colors whitespace-nowrap';
  const hasActiveFilter = genre !== 'all' || year !== 'all';

  if (baseList.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="该分类暂无内容"
          description="换个分类逛逛吧，也许有更多惊喜在等你。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: typeLabel }]} />

      <SectionTitle title={`${typeLabel}大全`} subtitle={descriptionText} asHeading="h1" />

      <div className="mb-6 rounded-lg border border-background-200 bg-background-100">
        <div className="flex items-center justify-between border-b border-background-200 px-4 py-3 md:hidden">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground-200">
            <i className="ri-filter-3-line text-primary-500" />
            筛选与排序
          </span>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="text-sm text-primary-500"
          >
            {filtersOpen ? '收起' : '展开'}
          </button>
        </div>

        <div className={`${filtersOpen ? 'block' : 'hidden'} px-4 py-4 md:block`}>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-sm text-foreground-500">类型</span>
            <button
              type="button"
              onClick={() => {
                setGenre('all');
                setPage(1);
              }}
              className={`${chipBase} ${
                genre === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-200 text-foreground-300 hover:text-foreground-50'
              }`}
            >
              全部
            </button>
            {genres.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => {
                  setGenre(g);
                  setPage(1);
                }}
                className={`${chipBase} ${
                  genre === g
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-200 text-foreground-300 hover:text-foreground-50'
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-sm text-foreground-500">年份</span>
            <button
              type="button"
              onClick={() => {
                setYear('all');
                setPage(1);
              }}
              className={`${chipBase} ${
                year === 'all'
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-200 text-foreground-300 hover:text-foreground-50'
              }`}
            >
              全部
            </button>
            {years.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => {
                  setYear(String(y));
                  setPage(1);
                }}
                className={`${chipBase} ${
                  String(y) === year
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-200 text-foreground-300 hover:text-foreground-50'
                }`}
              >
                {y}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm text-foreground-500">排序</span>
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => {
                    setSort(s.key);
                    setPage(1);
                  }}
                  className={`${chipBase} ${
                    sort === s.key
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-200 text-foreground-300 hover:text-foreground-50'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1 text-sm text-foreground-400 transition-colors hover:text-primary-500"
              >
                <i className="ri-close-circle-line" />
                清除筛选
              </button>
            )}
          </div>
        </div>
      </div>

      <p className="mb-4 text-sm text-foreground-500">
        共找到 <span className="font-semibold text-primary-500">{filtered.length}</span> 部作品
      </p>

      {pageItems.length === 0 ? (
        <EmptyState
          icon="ri-search-eye-line"
          title="暂无匹配的影视作品"
          description="当前筛选条件下没有找到内容，试试调整筛选条件。"
          actionLabel="清除筛选"
          onAction={resetFilters}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {pageItems.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}